import React, { useState, useEffect, useCallback, useRef, useMemo } from "react";
import {
  GoogleMap,
  useLoadScript,
  Marker,
  DirectionsRenderer,
  OverlayView,
} from "@react-google-maps/api";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import {
  Grid,
  Paper,
  Container,
  Typography,
  Box,
  Alert,
  Stack,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import config from "../../config.json";
import "./stops.css";
import BusIcon from "../../images/bus-svgrepo-com.svg";
// import BusIcon from "../../images/vehicle/Map_Vehicle_active.svg";
import { useTranslation } from "react-i18next";

const mapStyles = [
  { featureType: "all", elementType: "all", stylers: [{ visibility: "off" }] },
  {
    featureType: "road",
    elementType: "geometry",
    stylers: [{ visibility: "on" }, { color: "black" }],
  },
];

const colors = [
  "#20A8E0", "#8FBC8F", "#FFD700", "#6495ED", "#FF69B4", "#FFA07A",
  "#ADFF2F", "#FF4500", "#9370DB", "#FFFFFF",
];

const LoadingScreen = () => (
  <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "95vh", flexDirection: "column", gap: "20px" }}>
    <CircularProgress size={60} />
    <div>Loading map data...</div>
  </div>
);

const libraries = ["places", "directions"];

const parseCoordinate = (value) => (typeof value === "string" ? parseFloat(value) : isNaN(value) ? null : value);
const hasValidCoordinates = (stop) => parseCoordinate(stop.latitude) !== null && parseCoordinate(stop.longitude) !== null;

const StopsScreen = () => {
  const { t } = useTranslation();
  const { stopId } = useParams();
  const [isLoading, setIsLoading] = useState(true);
  const [isInitialized, setIsInitialized] = useState(false);
  const [stopsData, setStopsData] = useState([]);
  const [routeData, setRouteData] = useState([]);
  const [directions, setDirections] = useState([]);
  const [error, setError] = useState(null);
  const [labelPositions, setLabelPositions] = useState({});
  const [highlightedStopId, setHighlightedStopId] = useState(null);

  const [filterConfig, setFilterConfig] = useState([{ id: "route", label: "Route", options: [] }]);
  const [selectedRouteId, setSelectedRouteId] = useState(null);
  const [mapCenter, setMapCenter] = useState();
  const mapRef = useRef(null);

  const [vehicleData, setVehicleData] = useState({});
  const [vehicleHistory, setVehicleHistory] = useState({});
  const [stoppedVehicles, setStoppedVehicles] = useState({});
  const [allVehicles, setAllVehicles] = useState([]);
  const [calculatingDistances, setCalculatingDistances] = useState(false);

  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const isConnectedRef = useRef(false);
  const routePathsRef = useRef({});
  const navigate = useNavigate();

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: "AIzaSyD7rqUpTzUpEbxm-Xc7ikltFJGzOhd92Qk",
    libraries,
  });

  const processRouteCoordinates = (route) => {
    if (!route.routeCoordinates || !Array.isArray(route.routeCoordinates)) return [];
    return [...route.routeCoordinates].sort((a, b) => (a.order || 0) - (b.order || 0))
      .map(coord => ({ lat: parseCoordinate(coord.latitude) || 0, lng: parseCoordinate(coord.longitude) || 0 }));
  };

  const processUniqueRoutes = (stops) => {
    const routesMap = new Map();
    stops.forEach(stop => {
      if (stop.routes) stop.routes.forEach(route => {
        if (!routesMap.has(route.id)) routesMap.set(route.id, {
          id: route.id, name: route.name || `Route ${route.id}`, color: route.routeColor || "#20A8E0",
          coordinates: processRouteCoordinates(route), routeCoordinates: route.routeCoordinates || [],
        });
      });
    });
    return Array.from(routesMap.values());
  };

  const extractAllVehicles = (stops) => {
    const vehicles = [];
    stops.forEach(stop => {
      if (stop.routes) stop.routes.forEach(route => {
        if (route.vehicles) route.vehicles.forEach(vehicle => {
          if (!vehicles.some(v => v.device_id === vehicle.device_id)) vehicles.push({
            device_id: vehicle.device_id, id: vehicle.id, name: vehicle.name, routeId: route.id,
          });
        });
      });
    });
    return vehicles;
  };

  const calculateHeading = (prev, current) => (!prev || !current) ? null : window.google.maps.geometry.spherical.computeHeading(
    new window.google.maps.LatLng(prev.lat, prev.lng), new window.google.maps.LatLng(current.lat, current.lng)
  );

  const getBackgroundColor = (deviceId) => {
    const index = Math.abs(hashString(deviceId)) % colors.length;
    return colors[index];
  };

  const hashString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) hash = str.charCodeAt(i) + ((hash << 5) - hash) & hash;
    return hash;
  };

  const snapToRoute = useCallback((routeId, position) => {
    if (!routePathsRef.current[routeId]) return position;
    const path = routePathsRef.current[routeId].map(p => new window.google.maps.LatLng(p.lat, p.lng));
    let closestPoint = position;
    let minDistance = Infinity;
    for (const point of path) {
      const distance = window.google.maps.geometry.spherical.computeDistanceBetween(
        new window.google.maps.LatLng(position.lat, position.lng), point
      );
      if (distance < minDistance) {
        minDistance = distance;
        closestPoint = { lat: point.lat(), lng: point.lng() };
      }
    }
    return closestPoint;
  }, []);

  const calculateRouteDistance = useCallback((vehiclePosition, stopPosition, routeId) => {
    if (!mapRef.current || !routePathsRef.current[routeId]) return null;
    const routePath = routePathsRef.current[routeId].map(p => new window.google.maps.LatLng(p.lat, p.lng));
    if (!routePath.length) return null;
    const vehicleOnRoute = snapToRoute(routeId, vehiclePosition);
    const stopOnRoute = snapToRoute(routeId, stopPosition);
    let vehiclePathIndex = 0, stopPathIndex = 0, minVehicleDist = Infinity, minStopDist = Infinity;
    routePath.forEach((point, index) => {
      const vDist = window.google.maps.geometry.spherical.computeDistanceBetween(
        new window.google.maps.LatLng(vehicleOnRoute.lat, vehicleOnRoute.lng), point
      );
      const sDist = window.google.maps.geometry.spherical.computeDistanceBetween(
        new window.google.maps.LatLng(stopOnRoute.lat, stopOnRoute.lng), point
      );
      if (vDist < minVehicleDist) { minVehicleDist = vDist; vehiclePathIndex = index; }
      if (sDist < minStopDist) { minStopDist = sDist; stopPathIndex = index; }
    });
    let distance = 0;
    const startIdx = Math.min(vehiclePathIndex, stopPathIndex);
    const endIdx = Math.max(vehiclePathIndex, stopPathIndex);
    for (let i = startIdx; i < endIdx; i++) distance += window.google.maps.geometry.spherical.computeDistanceBetween(routePath[i], routePath[i + 1]);
    return distance;
  }, [snapToRoute]);

  const handleSSEMessage = useCallback(async (data) => {
    if (data.type === "connected") {
      console.log("SSE connected:", data);
      return;
    }

    const deviceId = data.device_id;
    if (!deviceId || !data.Latitude || !data.Longitude) {
      console.warn("Invalid SSE data structure:", data);
      return;
    }

    const position = { lat: parseCoordinate(data.Latitude), lng: parseCoordinate(data.Longitude) };
    if (isNaN(position.lat) || isNaN(position.lng)) {
      console.warn(`Invalid position for vehicle ${deviceId}`);
      return;
    }

    const vehicle = allVehicles.find(v => v.device_id === deviceId);
    if (!vehicle) {
      console.warn(`No vehicle found for device_id ${deviceId}`);
      return;
    }

    const newHistory = { ...vehicleHistory };
    const prevPosition = vehicleHistory[deviceId]?.position;
    const currentTime = Date.now();
    const lastUpdateTime = vehicleHistory[deviceId]?.timestamp || 0;
    const timeDiff = currentTime - lastUpdateTime;

    const newStoppedVehicles = { ...stoppedVehicles };
    if (prevPosition) {
      const distance = window.google.maps.geometry.spherical.computeDistanceBetween(
        new window.google.maps.LatLng(prevPosition.lat, prevPosition.lng),
        new window.google.maps.LatLng(position.lat, position.lng)
      );
      newStoppedVehicles[deviceId] = distance < 1 && timeDiff > 30000;
    }

    newHistory[deviceId] = { position, timestamp: currentTime };

    const snappedPosition = vehicle.routeId ? snapToRoute(vehicle.routeId, position) : position;
    const speed = parseFloat(data.Speed || data.speed || 0);
    const tracking = data.tracking || {};
    const routePath = tracking.routePath || [];
    routePathsRef.current[vehicle.routeId] = routePath.map(p => ({ lat: parseCoordinate(p.latitude), lng: parseCoordinate(p.longitude) }));

    const newVehicleData = {
      position: snappedPosition,
      routeId: vehicle.routeId,
      speed,
      heading: calculateHeading(prevPosition, snappedPosition),
      name: vehicle.name || deviceId,
      id: vehicle.id,
      distanceToSelectedStop: null, // Will calculate if highlightedStopId exists
    };

    if (highlightedStopId) {
      const selectedStop = stopsData.find(stop => stop.id === highlightedStopId);
      if (selectedStop && hasValidCoordinates(selectedStop)) {
        const stopPosition = { lat: parseCoordinate(selectedStop.latitude), lng: parseCoordinate(selectedStop.longitude) };
        const routeDistance = calculateRouteDistance(snappedPosition, stopPosition, vehicle.routeId);
        newVehicleData.distanceToSelectedStop = routeDistance !== null ? routeDistance / 1000 : null; // Convert to km
      }
    }

    setVehicleData(prev => ({ ...prev, [deviceId]: newVehicleData }));
    setVehicleHistory(newHistory);
    setStoppedVehicles(newStoppedVehicles);
    console.log("Updated vehicleData:", { [deviceId]: newVehicleData }); // Debug log
  }, [allVehicles, vehicleHistory, stoppedVehicles, snapToRoute, calculateHeading, highlightedStopId, stopsData]);

  useEffect(() => {
    if (allVehicles.length === 0) {
      console.log("No vehicle IDs, skipping SSE connection");
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
        eventSourceRef.current = null;
        isConnectedRef.current = false;
      }
      return;
    }

    const connectSSE = () => {
      if (isConnectedRef.current) return;
      const query = allVehicles.map(v => `vehicleId=${v.device_id}`).join("&");
      const sseUrl = `${config.baseURL}/redis/stream?${query}`;
      console.log(`Attempting to connect to SSE URL: ${sseUrl}`);

      if (eventSourceRef.current) eventSourceRef.current.close();
      const source = new EventSource(sseUrl);
      eventSourceRef.current = source;

      source.onopen = () => {
        console.log("SSE connection opened successfully");
        isConnectedRef.current = true;
      };

      source.onerror = (error) => {
        console.error("SSE connection error:", error);
        if (eventSourceRef.current) eventSourceRef.current.close();
        isConnectedRef.current = false;
        if (!reconnectTimeoutRef.current) {
          reconnectTimeoutRef.current = setTimeout(connectSSE, 5000);
        }
      };

      source.onmessage = (event) => {
        try {
          console.log("Received SSE message:", event.data);
          const data = JSON.parse(event.data);
          handleSSEMessage(data);
        } catch (e) {
          console.error("Error parsing SSE message:", e);
        }
      };
    };

    connectSSE();

    return () => {
      if (eventSourceRef.current) eventSourceRef.current.close();
      if (reconnectTimeoutRef.current) clearTimeout(reconnectTimeoutRef.current);
      isConnectedRef.current = false;
    };
  }, [allVehicles]);

  const fetchDirections = async (routes) => {
    if (!routes?.length) { setError("No routes available"); return; }
    const directionsService = new window.google.maps.DirectionsService();
    const directionsPromises = routes.map(route => new Promise((resolve, reject) => {
      if (!route.routeCoordinates || route.routeCoordinates.length < 2) return reject(`Route ${route.id} has insufficient coordinates`);
      const waypoints = route.routeCoordinates.slice(1, -1).map(coord => ({
        location: new window.google.maps.LatLng(parseCoordinate(coord.latitude), parseCoordinate(coord.longitude)),
        stopover: true,
      }));
      directionsService.route({
        origin: new window.google.maps.LatLng(parseCoordinate(route.routeCoordinates[0].latitude), parseCoordinate(route.routeCoordinates[0].longitude)),
        destination: new window.google.maps.LatLng(
          parseCoordinate(route.routeCoordinates[route.routeCoordinates.length - 1].latitude),
          parseCoordinate(route.routeCoordinates[route.routeCoordinates.length - 1].longitude)
        ),
        waypoints,
        travelMode: window.google.maps.TravelMode.DRIVING,
      }, (result, status) => status === window.google.maps.DirectionsStatus.OK
        ? (routePathsRef.current[route.id] = result.routes[0].overview_path.map(p => ({ lat: p.lat(), lng: p.lng() })), resolve({ result, id: route.id, color: route.color }))
        : reject(`Error fetching directions for route ${route.id}: ${status}`));
    }));
    try {
      const results = await Promise.all(directionsPromises);
      setDirections(results);
      setIsInitialized(true);
    } catch (error) {
      console.error("Error fetching directions:", error);
      setError("Failed to fetch route directions: " + error);
    }
  };

  const getRouteByStopCoordinates = async (lat, lng, orgId) => {
    try {
      const response = await axios.get(`${config.baseURL}/route/coordinates/`, { params: { longitude: lng, latitude: lat, organizationId: orgId || 17 } });
      return response.data;
    } catch (error) {
      console.error("Error fetching routes by coordinates:", error);
      return [];
    }
  };

  const focusOnStop = (stop) => {
    if (!stop || !hasValidCoordinates(stop)) return;
    setHighlightedStopId(stop.id);
    setTimeout(() => {
      if (mapRef.current) {
        const position = { lat: parseCoordinate(stop.latitude), lng: parseCoordinate(stop.longitude) };
        mapRef.current.panTo(position);
        mapRef.current.setZoom(18);
        setMapCenter(position);
      }
    }, 100);
  };

  const handleMapLoad = (map) => { mapRef.current = map; };

  useEffect(() => {
    if (isLoaded && stopsData.length > 0 && mapRef.current && highlightedStopId) {
      const selectedStop = stopsData.find(stop => stop.id.toString() === highlightedStopId.toString());
      if (selectedStop && hasValidCoordinates(selectedStop)) {
        const position = { lat: parseCoordinate(selectedStop.latitude), lng: parseCoordinate(selectedStop.longitude) };
        mapRef.current.panTo(position);
        mapRef.current.setZoom(19);
        if (selectedStop.routes?.length) {
          const routeIds = selectedStop.routes.map(route => route.id.toString());
          const routesToProcess = routeData.filter(route => routeIds.includes(route.id.toString()));
          if (routesToProcess.length) fetchDirections(routesToProcess);
        }
      }
    }
  }, [isLoaded, stopsData, highlightedStopId, routeData]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const stopsResponse = await axios.get(`${config.baseURL}/stop/organization`, { withCredentials: true });
        let stops = stopsResponse.data.map(stop => ({
          ...stop, latitude: parseCoordinate(stop.latitude), longitude: parseCoordinate(stop.longitude),
        })).filter(hasValidCoordinates);
        const stopsWithRoutes = await Promise.all(stops.map(async stop => ({
          ...stop, routes: await getRouteByStopCoordinates(stop.latitude, stop.longitude, stop.organizationId || 17),
        })));
        setStopsData(stopsWithRoutes);
        const uniqueRoutes = processUniqueRoutes(stopsWithRoutes);
        setRouteData(uniqueRoutes);
        const vehicles = extractAllVehicles(stopsWithRoutes);
        setAllVehicles(vehicles);

        if (stopId) {
          setHighlightedStopId(parseInt(stopId));
          const targetStop = stopsWithRoutes.find(stop => stop.id.toString() === stopId.toString());
          if (targetStop && hasValidCoordinates(targetStop)) {
            const position = { lat: parseCoordinate(targetStop.latitude), lng: parseCoordinate(targetStop.longitude) };
            setMapCenter(position);
            setTimeout(() => {
              if (mapRef.current) { mapRef.current.panTo(position); mapRef.current.setZoom(18); }
            }, 500);
            if (targetStop.routes?.length) setSelectedRouteId(null);
          }
        } else if (stopsWithRoutes.length) {
          setMapCenter({ lat: stopsWithRoutes[0].latitude, lng: stopsWithRoutes[0].longitude });
        }

        const uniqueRouteOptions = uniqueRoutes.map(route => ({ value: route.id.toString(), label: route.name }));
        setFilterConfig(prev => {
          const updated = [...prev];
          const routeIndex = updated.findIndex(f => f.id === "route");
          if (routeIndex >= 0) updated[routeIndex].options = uniqueRouteOptions;
          return updated;
        });

        setIsLoading(false);
      } catch (error) {
        if (error.response?.status === 401) { navigate("/"); return; }
        console.error("Error fetching initial data:", error);
        setError("Failed to load data: " + (error.message || "Unknown error"));
        setIsLoading(false);
      }
    };
    fetchInitialData();
  }, [navigate, stopId]);

  useEffect(() => {
    if (isLoaded && routeData.length > 0) {
      if (stopId) {
        const targetStop = stopsData.find(stop => stop.id.toString() === stopId.toString());
        if (targetStop?.routes?.length) {
          const routesToProcess = routeData.filter(route =>
            targetStop.routes.some(stopRoute => stopRoute.id.toString() === route.id.toString()));
          fetchDirections(routesToProcess);
        } else fetchDirections(routeData);
      } else if (selectedRouteId) {
        const routesToProcess = routeData.filter(route => route.id.toString() === selectedRouteId.toString());
        fetchDirections(routesToProcess);
      } else fetchDirections(routeData);
    }
  }, [isLoaded, routeData, selectedRouteId, stopId, stopsData]);

  const filteredStops = useMemo(() => {
    if (stopId) {
      const targetStop = stopsData.find(stop => stop.id.toString() === stopId.toString());
      if (targetStop?.routes?.length) {
        const relevantRouteIds = targetStop.routes.map(route => route.id.toString());
        return stopsData.filter(stop => hasValidCoordinates(stop) && stop.routes?.some(route => relevantRouteIds.includes(route.id.toString())));
      }
    }
    return selectedRouteId ? stopsData.filter(stop => hasValidCoordinates(stop) && stop.routes?.some(route => route.id.toString() === selectedRouteId.toString())) : stopsData.filter(hasValidCoordinates);
  }, [stopsData, selectedRouteId, stopId]);

  useEffect(() => {
    if (highlightedStopId) {
      const targetStop = stopsData.find(stop => stop.id.toString() === highlightedStopId.toString());
      if (targetStop?.routes?.length) setSelectedRouteId(null);
    }
  }, [highlightedStopId, stopsData]);

  if (loadError) return <div>Error loading Google Maps: {loadError.message}</div>;
  if (isLoading || !isLoaded) return <LoadingScreen />;

  return (
    <Container maxWidth="xl" sx={{ p: 0.5 }}>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <Grid container spacing={1} sx={{ display: "flex" }}>
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ height: "99vh", position: "relative" }}>
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={mapCenter}
              zoom={18}
              options={{
                styles: mapStyles, streetViewControl: false, mapTypeControl: false,
                fullscreenControl: false, zoomControl: false, draggable: false,
                scrollwheel: false, disableDoubleClickZoom: true, keyboardShortcuts: false,
                minZoom: 14.6, maxZoom: 20,
              }}
              onLoad={handleMapLoad}
            >
              {directions.map(direction => (
                <DirectionsRenderer
                  key={`direction-${direction.id}`}
                  directions={direction.result}
                  options={{ suppressMarkers: true, polylineOptions: { strokeColor: direction.color || colors[0], strokeOpacity: 0.8, strokeWeight: 5 } }}
                />
              ))}
              {filteredStops.map((stop, index) => {
                const isHighlighted = highlightedStopId && stop.id.toString() === highlightedStopId.toString();
                return (
                  <React.Fragment key={stop.id || `stop-${index}`}>
                    <OverlayView position={{ lat: parseCoordinate(stop.latitude), lng: parseCoordinate(stop.longitude) }} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                      <div style={{ position: "relative", top: "-69px", left: "0px" }}>
                        <div style={{
                          position: "absolute", left: "50%", transform: "translateX(-50%)",
                          backgroundColor: isHighlighted ? "#FFD700" : "white", padding: "4px 8px",
                          borderRadius: "4px", color: isHighlighted ? "black" : "black",
                          fontWeight: isHighlighted ? 600 : 400, fontSize: isHighlighted ? "16px" : "12px",
                          boxShadow: isHighlighted ? "0 2px 8px rgba(0,0,0,0.4)" : "0 2px 4px rgba(0,0,0,0.2)",
                          whiteSpace: "nowrap", zIndex: isHighlighted ? 1001 : 1000,
                          border: isHighlighted ? "2px solid #FF4500" : "none",
                        }}>{stop.name}</div>
                      </div>
                    </OverlayView>
                    <Marker
                      position={{ lat: stop.latitude, lng: stop.longitude }}
                      title={stop.name || `Stop ${index + 1}`}
                      icon={isHighlighted ? {
                        path: window.google.maps.SymbolPath.CIRCLE, scale: 12,
                        fillColor: "#FFD700", fillOpacity: 1, strokeColor: "#FF4500", strokeWeight: 3,
                      } : undefined}
                      onClick={() => focusOnStop(stop)}
                    />
                  </React.Fragment>
                );
              })}
              {Object.entries(vehicleData).map(([deviceId, data]) => (
                <React.Fragment key={deviceId}>
                  <OverlayView position={data.position} mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}>
                    <div style={{ position: "relative", top: "-96px", left: "0px", width: "2px", height: "2px" }}>
                      <div style={{
                        position: "absolute", width: "2px", height: "60px",
                        backgroundColor: getBackgroundColor(deviceId), left: "50%", top: "30px",
                        transform: "translateX(-50%)", zIndex: 2,
                      }} />
                      <div style={{
                        position: "absolute", bottom: `${-40 + (labelPositions[deviceId] || 0)}px`,
                        left: "50%", transform: "translateX(-50%)", color: "rgb(0, 0, 0)",
                        fontSize: "10px", fontWeight: 600, fontFamily: "Arial, sans-serif",
                        lineHeight: "14.06px", padding: "8px", textAlign: "left",
                        whiteSpace: "nowrap", backgroundColor: getBackgroundColor(deviceId),
                        borderRadius: "12px", zIndex: 1000, border: "1px solid black",
                      }}>{deviceId}</div>
                    </div>
                  </OverlayView>
                  <Marker
                    key={deviceId}
                    position={data.position}
                    title={`Vehicle ${deviceId}`}
                    icon={{ url: BusIcon, scaledSize: new window.google.maps.Size(30, 30), anchor: new window.google.maps.Point(15, 15) }}
                  />
                </React.Fragment>
              ))}
            </GoogleMap>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Box sx={{ borderRadius: "8px", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.15)", width: "100%", bgcolor: "#1a237e", color: "white" }}>
            <Box sx={{ padding: "16px 16px", background: "rgba(32, 168, 224, 0.2)", display: "flex", alignItems: "center", gap: "8px" }}>
              <Typography variant="h5" sx={{ color: "white", display: "flex", alignItems: "center", gap: "8px", fontWeight: "bold" }}>
                <DirectionsCarIcon /> {t("Next Vehicles")}
              </Typography>
            </Box>
            <Box sx={{ p: 1 }}>
              {Object.values(vehicleData).length > 0 ? (
                (() => {
                  const closestVehicle = [...Object.values(vehicleData)].sort((a, b) => {
                    if (a.distanceToSelectedStop === "loading") return 1;
                    if (b.distanceToSelectedStop === "loading") return -1;
                    return (a.distanceToSelectedStop ?? Infinity) - (b.distanceToSelectedStop ?? Infinity);
                  })[0];
                  return (
                    <Box sx={{ mb: 1, p: 1, borderRadius: 2, bgcolor: "rgba(32, 168, 224, 0.2)" }}>
                      <Stack spacing={1}>
                        <Box sx={{ p: 1, borderRadius: 2, bgcolor: "rgba(32, 168, 224, 0.2)", width: "100%" }}>
                          <Typography variant="h3" sx={{ fontWeight: "bold", color: "#F9E719", fontSize: { xs: "2.5rem", sm: "3rem", md: "3.5rem" }, textAlign: "center", lineHeight: 1.2 }}>
                            {closestVehicle.name}
                          </Typography>
                        </Box>
                        <Box sx={{ p: 3, borderRadius: 2, bgcolor: "rgba(255, 255, 255, 0.05)", width: "100%" }}>
                          <Typography variant="body1" sx={{ fontWeight: "medium", color: "#f5f5f5", mb: 1, fontSize: "2rem", textAlign: "center" }}>ETA</Typography>
                          {closestVehicle.distanceToSelectedStop === "loading" ? (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <CircularProgress size={30} sx={{ color: "#ffeb3b", mr: 2 }} />
                              <Typography variant="h4" sx={{ color: "#ffeb3b", fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" }, textAlign: "center" }}>Calculating...</Typography>
                            </Box>
                          ) : closestVehicle.distanceToSelectedStop !== null ? (
                            <Typography variant="h3" sx={{ fontWeight: "bold", color: "#ffeb3b", fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" }, lineHeight: 1.2, textAlign: "center" }}>
                              {formatETA(closestVehicle.distanceToSelectedStop, closestVehicle.speed)}
                            </Typography>
                          ) : (
                            <Typography variant="h4" sx={{ color: "#ffeb3b", fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" } }}>Unknown</Typography>
                          )}
                        </Box>
                        <Box sx={{ p: 3, borderRadius: 2, bgcolor: "rgba(255, 255, 255, 0.05)", width: "100%" }}>
                          <Typography variant="body1" sx={{ fontWeight: "medium", color: "#f5f5f5", mb: 1, fontSize: "2rem", textAlign: "center" }}>Distance</Typography>
                          {closestVehicle.distanceToSelectedStop === "loading" ? (
                            <Box sx={{ display: "flex", alignItems: "center" }}>
                              <CircularProgress size={30} sx={{ color: "#ffeb3b", mr: 2 }} />
                              <Typography variant="h4" sx={{ color: "#ffeb3b", fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" }, textAlign: "center" }}>Calculating...</Typography>
                            </Box>
                          ) : closestVehicle.distanceToSelectedStop !== null ? (
                            <Typography variant="h3" sx={{ fontWeight: "bold", color: "#ffeb3b", fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" }, lineHeight: 1, textAlign: "center" }}>
                              {closestVehicle.distanceToSelectedStop.toFixed(2)} km
                            </Typography>
                          ) : (
                            <Typography variant="h4" sx={{ color: "#ffeb3b", fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" } }}>Unknown</Typography>
                          )}
                        </Box>
                      </Stack>
                    </Box>
                  );
                })()
              ) : (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "150px" }}>
                  <Typography variant="h5" sx={{ fontSize: "1.5rem" }}>
                    {calculatingDistances ? "Calculating distances..." : t("No vehicle data available")}
                  </Typography>
                  {calculatingDistances && <CircularProgress size={30} sx={{ ml: 2, color: "white" }} />}
                </Box>
              )}
            </Box>
          </Box>
        </Grid>
      </Grid>
    </Container>
  );
};

// Helper function to format ETA based on distance and speed
const formatETA = (distanceKm, speedKmh) => {
  if (distanceKm === null || speedKmh <= 0) return "1m 0s";
  const timeSeconds = (distanceKm / speedKmh) * 3600;
  const minutes = Math.floor(timeSeconds / 60);
  const seconds = Math.floor(timeSeconds % 60);
  return `${minutes}m ${seconds}s`;
};

export default StopsScreen;